import { useRef, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { useLocale } from "../i18n/LocaleContext";
import { CATEGORIES, TREATMENT_IMAGES_EXTRA } from "../data/treatments";
import { Reveal, MaskedLines, Eyebrow, ChapterNo } from "../components/Reveal";
import { Marquee } from "../components/Marquee";
import { Lightbox } from "../components/Lightbox";

const GALLERY = [
  { key: "facial", src: TREATMENT_IMAGES_EXTRA.facialHands, alt: "Leidiane bei einer Gesichtsbehandlung" },
  { key: "makeup2", src: "/assets/img/lei-makeup-2.jpg", alt: "Leidiane Alcântara — Make-up Artist" },
  { key: "stones", src: "/assets/img/lei-stones.jpg", alt: "Leidiane Alcântara — Hot-Stone-Ritual" },
  { key: "portrait", src: TREATMENT_IMAGES_EXTRA.portrait2, alt: "Leidiane Alcântara" },
  { key: "makeup", src: TREATMENT_IMAGES_EXTRA.makeupClient, alt: "Leidiane beim professionellen Make-up" },
  { key: "makeup3", src: "/assets/img/lei-makeup-3.jpg", alt: "Leidiane Alcântara — Make-up" },
  { key: "roller", src: "/assets/img/lei-roller.jpg", alt: "Leidiane Alcântara — Massage" },
];

function GalleryVideo({ className = "h-auto w-full" }) {
  const ref = useRef(null);
  useEffect(() => {
    const v = ref.current;
    if (!v) return;
    const tryPlay = () => v.play().catch(() => {});
    tryPlay();
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) tryPlay(); });
    }, { threshold: 0.15 });
    io.observe(v);
    const evs = ["touchstart", "pointerdown", "scroll"];
    evs.forEach((ev) => window.addEventListener(ev, tryPlay, { once: true, passive: true }));
    return () => {
      io.disconnect();
      evs.forEach((ev) => window.removeEventListener(ev, tryPlay));
    };
  }, []);
  return (
    <video
      ref={ref}
      src={TREATMENT_IMAGES_EXTRA.video}
      poster={TREATMENT_IMAGES_EXTRA.videoPoster}
      autoPlay
      muted
      loop
      playsInline
      preload="metadata"
      data-testid="gallery-video-item"
      aria-label="Leidiane im Studio"
      className={className}
    />
  );
}

export default function Home() {
  const { t, link, lang } = useLocale();
  const [lightbox, setLightbox] = useState(null);
  const heroRef = useRef(null);
  const { scrollYProgress: heroProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const heroImgY = useTransform(heroProgress, [0, 1], [0, 90]);

  const auszeitRef = useRef(null);
  const videoRef = useRef(null);
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const tryPlay = () => v.play().catch(() => {});
    tryPlay();
    const events = ["touchstart", "pointerdown", "scroll"];
    events.forEach((ev) => window.addEventListener(ev, tryPlay, { once: true, passive: true }));
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) tryPlay(); });
    }, { threshold: 0.15 });
    io.observe(v);
    return () => {
      events.forEach((ev) => window.removeEventListener(ev, tryPlay));
      io.disconnect();
    };
  }, []);
  const { scrollYProgress: auszeitProgress } = useScroll({ target: auszeitRef, offset: ["start end", "end start"] });
  const auszeitY = useTransform(auszeitProgress, [0, 1], ["-8%", "8%"]);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} data-testid="hero-section" className="relative overflow-hidden">
        <div className="mx-auto grid min-h-screen max-w-[1600px] items-center gap-12 px-6 pb-16 pt-32 sm:px-10 lg:grid-cols-12 lg:gap-6 lg:px-16 lg:pt-20">
          <div className="lg:col-span-7">
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
              className="mb-8 text-[11px] font-label uppercase tracking-[0.4em] text-imperium-bronze"
              data-testid="hero-eyebrow"
            >
              {t("home.heroEyebrow")}
            </motion.p>
            <h1 className="font-serif font-light leading-[1.04] text-imperium-black text-[13vw] sm:text-6xl lg:text-7xl xl:text-[5.6rem]" data-testid="hero-title">
              <MaskedLines lines={t("home.heroLines")} />
            </h1>
            <motion.p
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.85, ease: [0.22, 1, 0.36, 1] }}
              className="mt-7 font-serif text-2xl font-light text-imperium-black/80 sm:text-3xl"
              data-testid="hero-welcome"
            >
              {t("home.heroWelcomePre")}{" "}
              <span className="italic tracking-[0.1em] text-imperium-gold">{t("home.heroWelcomeBrand")}</span>
            </motion.p>
            <motion.p
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 1.0, ease: [0.22, 1, 0.36, 1] }}
              className="mt-6 max-w-md text-base font-light leading-relaxed text-imperium-black/70"
              data-testid="hero-subtitle"
            >
              {t("home.heroSub")}
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 1.05, ease: [0.22, 1, 0.36, 1] }}
              className="mt-12 flex flex-wrap items-center gap-8"
            >
              <Link
                to={link("contact")}
                data-testid="hero-cta-book"
                className="border border-imperium-bronze px-8 py-4 text-[11px] font-label uppercase tracking-[0.25em] text-imperium-brown transition-colors duration-300 hover:bg-imperium-brown hover:text-imperium-white"
              >
                {t("cta.book")}
              </Link>
              <Link to={link("treatments")} data-testid="hero-cta-discover" className="link-line text-[11px] font-label uppercase tracking-[0.25em] text-imperium-brown">
                {t("cta.discover")}
              </Link>
            </motion.div>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1.2, delay: 1.4 }}
              className="mt-16 max-w-sm font-serif italic text-lg leading-relaxed text-imperium-bronze/90"
              data-testid="hero-quote"
            >
              {t("home.heroQuote")}
            </motion.p>
          </div>
          <div className="relative lg:col-span-5">
            <motion.div
              initial={{ clipPath: "inset(100% 0 0 0)" }}
              animate={{ clipPath: "inset(0% 0 0 0)" }}
              transition={{ duration: 1.4, delay: 0.35, ease: [0.22, 1, 0.36, 1] }}
              className="relative"
            >
              <motion.div style={{ y: heroImgY }} className="relative">
                <img
                  src={TREATMENT_IMAGES_EXTRA.hero}
                  alt="IMPERIUM Beauty & Wellness"
                  data-testid="hero-image"
                  className="h-[52vh] w-full object-cover lg:h-[74vh]"
                  fetchpriority="high"
                />
              </motion.div>
              <div className="pointer-events-none absolute -bottom-5 -left-5 -right-5 -top-5 border border-imperium-champagne/60" aria-hidden="true" />
            </motion.div>
          </div>
        </div>
      </section>

      <Marquee items={t("home.marquee")} />

      {/* 01 VERTRAUEN */}
      <section data-testid="trust-section" className="mx-auto max-w-[1600px] px-6 py-24 sm:px-10 lg:px-16 lg:py-36">
        <div className="grid gap-14 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <Reveal>
              <div className="flex items-baseline gap-4">
                <ChapterNo>{t("home.trustNo")}</ChapterNo>
                <Eyebrow>{t("home.trustEyebrow")}</Eyebrow>
              </div>
              <h2 className="mt-6 font-serif text-4xl font-light leading-tight text-imperium-black sm:text-5xl">{t("home.trustTitle")}</h2>
            </Reveal>
            <Reveal delay={0.12}>
              <p className="mt-8 max-w-xl text-base font-light leading-relaxed text-imperium-black/70">{t("home.trustText")}</p>
            </Reveal>
            <Reveal delay={0.2}>
              <ul className="mt-12 max-w-xl">
                {t("home.trustPoints").map((point, i) => (
                  <li key={i} className="flex items-center gap-5 border-t border-imperium-champagne/40 py-4 last:border-b" data-testid={`trust-point-${i}`}>
                    <span className="h-1 w-1 rotate-45 bg-imperium-gold" aria-hidden="true" />
                    <span className="text-sm uppercase tracking-[0.2em] text-imperium-black/80">{point}</span>
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>
          <div className="lg:col-span-5">
            <Reveal delay={0.15} className="lg:mt-24">
              <div className="overflow-hidden">
                <img src={TREATMENT_IMAGES_EXTRA.consult} alt="Persönliche Beratung bei IMPERIUM" loading="lazy" className="aspect-[4/5] w-full object-cover transition-transform duration-700 hover:scale-[1.03]" />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* 02 BEHANDLUNGEN */}
      <section data-testid="treatments-section" className="bg-imperium-offwhite">
        <div className="mx-auto max-w-[1600px] px-6 py-24 sm:px-10 lg:px-16 lg:py-36">
          <Reveal>
            <div className="flex items-baseline gap-4">
              <ChapterNo>{t("home.servicesNo")}</ChapterNo>
              <Eyebrow>{t("home.servicesEyebrow")}</Eyebrow>
            </div>
            <div className="mt-6 flex flex-wrap items-end justify-between gap-8">
              <h2 className="max-w-2xl font-serif text-4xl font-light leading-tight text-imperium-black sm:text-5xl">{t("home.servicesTitle")}</h2>
              <p className="max-w-xs text-sm font-light leading-relaxed text-imperium-black/60">{t("home.servicesIntro")}</p>
            </div>
          </Reveal>
          <div className="mt-16">
            {CATEGORIES.map((cat, i) => (
              <Reveal key={cat.key} delay={0.04 * i}>
                <Link
                  to={link("treatments", cat.slugs[lang])}
                  data-testid={`category-row-${cat.key}`}
                  className="group flex items-center justify-between gap-6 border-t border-imperium-champagne/40 py-6 transition-all duration-500 last:border-b hover:bg-imperium-cream/50 hover:pl-4 sm:py-8"
                >
                  <span className="flex items-baseline gap-6 sm:gap-10">
                    <span className="text-[11px] font-label tracking-[0.2em] text-imperium-champagne">{String(i + 1).padStart(2, "0")}</span>
                    <span className="font-serif text-2xl font-light text-imperium-black transition-colors group-hover:text-imperium-bronze sm:text-4xl">{cat.names[lang]}</span>
                  </span>
                  <span className="flex items-center gap-6">
                    <span className="hidden max-w-[220px] text-right text-[10px] font-label uppercase tracking-[0.2em] text-imperium-bronze/70 md:block">
                      {cat.desc[lang]}
                    </span>
                    <ArrowUpRight className="h-5 w-5 shrink-0 text-imperium-bronze transition-transform duration-500 group-hover:rotate-45" />
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* 03 AUSZEIT + ABLAUF */}
      <section ref={auszeitRef} data-testid="auszeit-section" className="relative flex min-h-[75vh] items-center overflow-hidden">
        <motion.video
          ref={videoRef}
          src={TREATMENT_IMAGES_EXTRA.video}
          poster={TREATMENT_IMAGES_EXTRA.videoPoster}
          style={{ y: auszeitY }}
          className="absolute inset-0 h-[116%] w-full object-cover"
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          aria-hidden="true"
          data-testid="auszeit-video"
        />
        <div className="absolute inset-0 bg-imperium-brown/55" aria-hidden="true" />
        <div className="relative mx-auto max-w-3xl px-6 py-32 text-center">
          <Reveal>
            <Eyebrow className="text-imperium-champagne">{t("home.auszeitEyebrow")}</Eyebrow>
            <h2 className="mt-6 font-serif text-3xl font-light leading-snug text-imperium-white sm:text-5xl">{t("home.auszeitTitle")}</h2>
            <p className="mx-auto mt-8 max-w-xl text-base font-light leading-relaxed text-imperium-white/80">{t("home.auszeitText")}</p>
            <Link
              to={link("contact")}
              data-testid="auszeit-cta"
              className="mt-12 inline-block border border-imperium-champagne px-8 py-4 text-[11px] font-label uppercase tracking-[0.25em] text-imperium-champagne transition-colors duration-300 hover:bg-imperium-champagne hover:text-imperium-brown"
            >
              {t("cta.request")}
            </Link>
          </Reveal>
        </div>
      </section>

      <section data-testid="steps-section" className="mx-auto max-w-[1600px] px-6 py-24 sm:px-10 lg:px-16 lg:py-36">
        <Reveal>
          <Eyebrow>{t("home.ablaufEyebrow")}</Eyebrow>
          <h2 className="mt-6 max-w-2xl font-serif text-4xl font-light leading-tight text-imperium-black sm:text-5xl">{t("home.ablaufTitle")}</h2>
        </Reveal>
        <div className="mt-16 grid gap-10 sm:grid-cols-2 lg:grid-cols-5 lg:gap-8">
          {t("home.steps").map((step, i) => (
            <Reveal key={i} delay={0.08 * i}>
              <div className="border-t border-imperium-champagne/60 pt-6" data-testid={`step-${i}`}>
                <span className="font-serif italic text-3xl text-imperium-champagne">{String(i + 1).padStart(2, "0")}</span>
                <h3 className="mt-4 font-serif text-xl text-imperium-black">{step.t}</h3>
                <p className="mt-3 text-sm font-light leading-relaxed text-imperium-black/60">{step.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* 04 IHRE EXPERTIN */}
      <section data-testid="expert-section" className="bg-imperium-offwhite">
        <div className="mx-auto grid max-w-[1600px] items-center gap-14 px-6 py-24 sm:px-10 lg:grid-cols-12 lg:px-16 lg:py-36">
          <div className="lg:col-span-6">
            <Reveal>
              <div className="relative max-w-md">
                <img src="/assets/img/lei-sobre.jpg" alt="Leidiane Alcântara" data-testid="expert-photo" loading="lazy" className="aspect-[4/5] w-full object-cover" style={{ objectPosition: "center top" }} />
                <div className="pointer-events-none absolute -bottom-5 -right-5 -top-5 left-5 border border-imperium-gold/50" aria-hidden="true" />
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-6 lg:pl-10">
            <Reveal>
              <div className="flex items-baseline gap-4">
                <ChapterNo>{t("home.expertNo")}</ChapterNo>
                <Eyebrow>{t("home.expertEyebrow")}</Eyebrow>
              </div>
              <h2 className="mt-6 font-serif text-5xl font-light text-imperium-black sm:text-6xl">{t("home.expertName")}</h2>
              <div className="mt-6 h-px w-24 bg-imperium-gold" aria-hidden="true" />
              <p className="mt-8 max-w-lg text-base font-light leading-relaxed text-imperium-black/70">{t("home.expertText")}</p>
              <Link to={link("about")} data-testid="expert-cta" className="link-line mt-10 inline-block text-[11px] font-label uppercase tracking-[0.25em] text-imperium-brown">
                {t("cta.moreAbout")}
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* 05 STUDIO */}
      <section data-testid="studio-teaser" className="mx-auto grid max-w-[1600px] items-center gap-14 px-6 py-24 sm:px-10 lg:grid-cols-12 lg:px-16 lg:py-36">
        <div className="order-2 lg:order-1 lg:col-span-6 lg:pr-10">
          <Reveal>
            <div className="flex items-baseline gap-4">
              <ChapterNo>{t("home.studioNo")}</ChapterNo>
              <Eyebrow>{t("home.studioEyebrow")}</Eyebrow>
            </div>
            <h2 className="mt-6 max-w-lg font-serif text-4xl font-light leading-tight text-imperium-black sm:text-5xl">{t("home.studioTitle")}</h2>
            <p className="mt-8 max-w-lg text-base font-light leading-relaxed text-imperium-black/70">{t("home.studioText")}</p>
            <Link to={link("studio")} data-testid="studio-teaser-cta" className="link-line mt-10 inline-block text-[11px] font-label uppercase tracking-[0.25em] text-imperium-brown">
              {t("cta.discoverStudio")}
            </Link>
          </Reveal>
        </div>
        <div className="order-1 lg:order-2 lg:col-span-6">
          <Reveal>
            <div className="overflow-hidden">
              <img src={TREATMENT_IMAGES_EXTRA.treatmentRoom} alt="IMPERIUM Studio" loading="lazy" className="aspect-[4/5] w-full object-cover transition-transform duration-700 hover:scale-[1.03]" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* GALERIE */}
      <section data-testid="gallery-section" className="mx-auto max-w-[1600px] px-6 pb-24 sm:px-10 lg:px-16 lg:pb-36">
        <Reveal>
          <Eyebrow>{t("home.galleryEyebrow")}</Eyebrow>
          <h2 className="mt-6 font-serif text-4xl font-light text-imperium-black sm:text-5xl">{t("home.galleryTitle")}</h2>
        </Reveal>
        <div className="mt-14 grid grid-cols-2 gap-3 lg:grid-cols-4" data-testid="gallery-grid">
          {GALLERY.slice(0, 3).map((img, i) => (
            <Reveal key={img.key} delay={0.05 * i}>
              <button
                type="button"
                data-testid={`gallery-item-${img.key}`}
                aria-label={img.alt}
                onClick={() => setLightbox(i)}
                className="group block w-full cursor-zoom-in overflow-hidden"
              >
                <img src={img.src} alt={img.alt} loading="lazy" className="aspect-[3/4] w-full object-cover object-top transition-transform duration-700 group-hover:scale-[1.04]" />
              </button>
            </Reveal>
          ))}
          <Reveal delay={0.15}>
            <div className="overflow-hidden">
              <GalleryVideo className="aspect-[3/4] w-full object-cover object-top" />
            </div>
          </Reveal>
          {GALLERY.slice(3).map((img, i) => (
            <Reveal key={img.key} delay={0.05 * (i + 4)}>
              <button
                type="button"
                data-testid={`gallery-item-${img.key}`}
                aria-label={img.alt}
                onClick={() => setLightbox(i + 3)}
                className="group block w-full cursor-zoom-in overflow-hidden"
              >
                <img src={img.src} alt={img.alt} loading="lazy" className="aspect-[3/4] w-full object-cover object-top transition-transform duration-700 group-hover:scale-[1.04]" />
              </button>
            </Reveal>
          ))}
        </div>
        <Lightbox images={GALLERY} index={lightbox} onClose={() => setLightbox(null)} onNavigate={setLightbox} />
      </section>

      {/* FINAL CTA */}
      <section data-testid="final-cta-section" className="bg-imperium-brown">
        <div className="mx-auto max-w-3xl px-6 py-24 text-center lg:py-36">
          <Reveal>
            <img src="/assets/img/logo-emblem.png" alt="" className="mx-auto mb-10 h-14 w-auto opacity-80" loading="lazy" />
            <h2 className="font-serif text-4xl font-light leading-tight text-imperium-white sm:text-5xl">{t("home.finalTitle")}</h2>
            <p className="mx-auto mt-6 max-w-md text-base font-light leading-relaxed text-imperium-white/70">{t("home.finalText")}</p>
            <div className="mt-12 flex flex-wrap items-center justify-center gap-5">
              <Link
                to={link("contact")}
                data-testid="final-cta-book"
                className="border border-imperium-champagne px-8 py-4 text-[11px] font-label uppercase tracking-[0.25em] text-imperium-champagne transition-colors duration-300 hover:bg-imperium-champagne hover:text-imperium-brown"
              >
                {t("cta.book")}
              </Link>
              <a href="tel:+41786136096" data-testid="final-cta-call" className="link-line text-[11px] font-label uppercase tracking-[0.25em] text-imperium-white/80">
                {t("cta.call")}
              </a>
              <a href="https://wa.me/41786136096" target="_blank" rel="noopener noreferrer" data-testid="final-cta-whatsapp" className="link-line text-[11px] font-label uppercase tracking-[0.25em] text-imperium-white/80">
                {t("cta.whatsapp")}
              </a>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
